import { Component, inject, OnInit, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ContactService } from '../../../core/services/contacInfo/contact.service';
import { SocialItem } from '../../../core/models/ibranches-and-social';
import { PermissionService } from '../../../core/permissions/permission.service';
import { PERMISSIONS } from '../../../core/permissions/permission.constants';
import { SubscribeService } from '../../../core/services/subscribe/subscribe.service';
import { ToasterService } from '../../../core/services/ui-services.ts/toaster.service';
import { AuthService } from '../../../core/auth/auth.service';

@Component({
  selector: 'app-footer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, CommonModule, RouterModule, ReactiveFormsModule, NgOptimizedImage],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent implements OnInit {
  private contactService = inject(ContactService);
  private permissionService = inject(PermissionService);
  private subscribeService = inject(SubscribeService);
  private toast = inject(ToasterService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  
  // Expose userProfile signal for template check
  readonly userProfile = this.authService.userProfile;
  
  // Expose PERMISSIONS constant for template access
  readonly PERMISSIONS = PERMISSIONS;
  
  socialItem = signal<SocialItem[]>([]);
  
  // Computed signal for template iteration (better type inference)
  readonly socialItems = computed(() => this.socialItem());

  readonly subscribeForm = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });

  ngOnInit(): void {
    // Since footer is deferred with @defer (on viewport), ngOnInit only runs when footer is visible
    // Load social media immediately (non-blocking)
    this.loadSocialMedia();
    console.log(this.userProfile());
  }

  // Helper method to check if user has required permissions
  hasPermission(requiredPermissions: bigint[]): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) {
      return true;
    }
    
    return this.permissionService.hasAnyPermission(...requiredPermissions);
  }

  loadSocialMedia(): void {
    this.contactService.getSortedSocialLinks().subscribe({
      next: (items) => this.socialItem.set(items),
      error: () => this.socialItem.set([])
    });
  }

  submit(): void {
    if (this.subscribeForm.invalid) {
      this.toast.error(this.translate.instant('Validation.InvalidEmail'));
      return;
    }

    const payload = this.subscribeForm.value;
    this.subscribeService.addsubscriber(payload).subscribe({
      next: () => {
        this.toast.success(this.translate.instant('Validation.SubscribeSuccess'));
        this.subscribeForm.reset();
      },
      error: (err) => {
        const errorMessage = err.error?.errorMessages || this.translate.instant('Home.empty');
        this.toast.error(errorMessage);
      },
    });
  }
}
